import { useEffect, useMemo, useState } from 'react';
import { businessInfo } from '../mockData';

const DEFAULT_PRIVACY_POLICY_TEXT =
  "Jena Schirmer is committed to protecting your privacy. This policy explains how personal data is collected, used, and protected when you use coaching and therapeutic massage services.\n\nCollected data may include contact details, appointment information, service-related notes, and billing details where applicable.\n\nYour information is used to deliver services, manage appointments, communicate with you, and comply with legal obligations.\n\nAs services are offered in the European Union, GDPR principles are followed. You may request access, correction, deletion, or portability of your data where legally applicable.\n\nData is stored using reasonable technical and organizational safeguards. No internet system is fully secure, but protective measures are continuously maintained.\n\nThis policy may be updated from time to time. The latest version is always published on this page.";

const DEFAULT_TERMS_OF_SERVICE_TEXT = `By booking or using these services, you agree to these Terms of Service.

Services include mental health and wellbeing coaching, therapeutic massage, and workshops or training.

Appointments must be scheduled in advance and are subject to confirmation.

Coaching and massage services support wellbeing and are not a substitute for emergency medical care.

Cancellations should be made at least 24 hours in advance. Late cancellations or no-shows may incur fees.

Payment terms and service fees are confirmed before or during booking.

All shared information is treated confidentially, except where disclosure is legally required.

These terms may be updated over time. Continued use of services means acceptance of the latest version.`;

const DEFAULT_SITE_SETTINGS = {
  businessName: businessInfo.name || 'Jena Schirmer',
  businessTagline: 'Wellness & Coaching',
  contactEmail: businessInfo.email || 'jaz.and.u.art@gmail.com',
  contactPhone: businessInfo.phone || '+49 157 3877 3842',
  locationLabel: businessInfo.location || 'Munich, Germany',
  serviceAreaLabel: businessInfo.serviceArea || 'Munich (In-person) | Worldwide (Online Coaching)',
  footerDescription:
    'Professional Mental Health Coaching & Therapeutic Massage Services in Munich and Online Worldwide.',
  bookingHelpText:
    'If you have questions or prefer to schedule by phone, feel free to contact me directly.',
  officeHoursWeekdayLabel: 'Monday - Friday:',
  officeHoursWeekdayHours: '9:00 AM - 7:00 PM',
  officeHoursSaturdayLabel: 'Saturday:',
  officeHoursSaturdayHours: '10:00 AM - 4:00 PM',
  officeHoursSundayLabel: 'Sunday:',
  officeHoursSundayHours: 'By Appointment',
  whatsappNumber: businessInfo.phone || '+49 157 3877 3842',
  whatsappMessage: "Hello, I'd like to book a session.",
  featuredImageUrl: 'https://images.unsplash.com/photo-1616849068479-376693e487d2',
  siteExcerpt:
    'Professional mental health coaching and therapeutic massage services in Munich and online worldwide.',
  enableManualPayments: true,
  manualPaymentInstructions:
    'Manual payment is available after booking confirmation (for example bank transfer or cash).',
  enablePaypalPayments: false,
  paypalClientId: '',
  paypalEnvironment: 'sandbox',
  paypalCurrency: 'EUR',
  onlinePaymentTaxPercent: 19,
  onlinePaymentBookingFee: 2.5,
  onlinePaymentMinimumPercent: 50,
  cancellationWindowHours: 24,
  cancellationTerms:
    'A minimum booking payment of 50% is required when booking online, or you may pay the full amount. If a booking is cancelled within the cancellation window, no refund is provided.',
  bookingBufferMinutes: 60,
  offDays: [],
  privacyPolicyText: DEFAULT_PRIVACY_POLICY_TEXT,
  termsOfServiceText: DEFAULT_TERMS_OF_SERVICE_TEXT,
};

const toPhoneHref = (phone) => {
  const normalized = String(phone || '').replace(/[^\d+]/g, '');
  return normalized ? `tel:${normalized}` : 'tel:';
};

const getInitials = (name) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) {
    return 'JS';
  }

  return parts
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
};

const fetchSiteSettings = async () => {
  const response = await fetch('/api/content/site-settings', {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to load site settings: ${response.status}`);
  }

  return response.json();
};

export const useSiteSettings = () => {
  const [siteSettings, setSiteSettings] = useState(DEFAULT_SITE_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isCancelled = false;

    const load = async () => {
      try {
        const payload = await fetchSiteSettings();
        if (isCancelled || !payload || typeof payload !== 'object') {
          return;
        }

        setSiteSettings((current) => ({
          ...current,
          ...payload,
        }));
      } catch (error) {
        if (!isCancelled) {
          console.error('Failed to load site settings from backend:', error);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      isCancelled = true;
    };
  }, []);

  const derived = useMemo(
    () => ({
      ...siteSettings,
      contactPhoneHref: toPhoneHref(siteSettings.contactPhone),
      brandInitials: getInitials(siteSettings.businessName),
    }),
    [siteSettings],
  );

  return {
    siteSettings: derived,
    isLoading,
  };
};
